import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { VisaTutorParams, VisaTutorResponse } from '@/lib/types';

/**
 * The AI Visa Tutor page's data layer: one read of
 * `GET /product-insights/visa-tutor` per (cycle, destination country). SEED's
 * own figures through EMERGE's proxy (server cache, nothing stored).
 *
 * - The read waits for a cycle: the page resolves SEED FAB's current one first
 *   (same business-cycle ids), so the default view is the current cycle rather
 *   than an unbounded one.
 * - Search is a client-side view and never reaches this hook.
 * - Previous data stays on screen while a new key loads (`refreshing`);
 *   `loading` is only true when there is nothing to show yet. Loading is
 *   derived from request keys, never set inside the effect.
 */

interface VisaTutorResult {
  key: string;
  data: VisaTutorResponse | null;
  error: unknown | null;
}

export function useVisaTutorSummary(
  cycleId: number | null,
  countryIso2: string | null,
  reloadToken: number,
): {
  data: VisaTutorResponse | null;
  loading: boolean;
  error: unknown | null;
  refreshing: boolean;
} {
  const paramsKey = cycleId === null ? null : `${cycleId}|${countryIso2 ?? ''}`;
  const requestKey = paramsKey === null ? null : `${paramsKey}#${reloadToken}`;
  const [lastData, setLastData] = useState<VisaTutorResponse | null>(null);
  const [result, setResult] = useState<VisaTutorResult | null>(null);

  useEffect(() => {
    if (cycleId === null) return;
    const controller = new AbortController();
    const { signal } = controller;
    const key = `${cycleId}|${countryIso2 ?? ''}#${reloadToken}`;
    const params: VisaTutorParams =
      countryIso2 === null ? { cycleId } : { cycleId, countryIso2 };
    api
      .productInsightsVisaTutor(params, signal)
      .then((data) => {
        if (signal.aborted) return;
        setLastData(data);
        setResult({ key, data, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setResult({ key, data: null, error: err });
      });
    return () => controller.abort();
  }, [cycleId, countryIso2, reloadToken]);

  const current =
    result !== null && requestKey !== null && result.key === requestKey ? result : null;
  const settled = current !== null;
  return {
    data: lastData,
    loading: lastData === null && !settled,
    error: current?.error ?? null,
    refreshing: lastData !== null && !settled,
  };
}
